import {Box, Collapse, List, Typography} from '@mui/material';
import AurButton from '../../../common/aur-button/AurButton';
import AurTagSelectList from '../../bulk-tagging/AurTagSelectList';
import {t} from 'i18next';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {ReactComponent as PlusIcon} from '../../../../assests/svg/plus-icon-small.svg';
import AddNewTag from './AddNewTag';
import AurLoadingIndicator from '../../../common/aur-loading-indicator/AurLoadingIndictor';
import {getNextDisplayOrder} from '../../tag-utils';
import {
  IUpdateTagNamesBody,
  useUpdateTagNames,
} from '../../../../hooks/eca-tagging/useUpdateTagNames';
import {IFieldModal} from '../../../pages/field-management/custom-field/constant';
import {ITag} from '../../../pages/field-management/custom-field/DraggableTag';
import {
  PAGE_NAME,
  usePermissionsByPage,
} from '../../../../hooks/permissions/usePermissionsByPage';

interface ITagList {
  index: number;
  tagField: IFieldModal;
  hideHeader?: boolean;
  onSelectionUpdate?: (tags: ITag[]) => void;
  navigationScreen?: 'userAppliedTag' | 'selectFieldAndApplyTag';
  setHidefields?: (value: boolean) => void;
  addNewTagToList: (tag: ITag) => void;
  defaultSelectedTags?: ITag[];
  activeDocs?: string;
  indexName?: string;
  onMutationSuccess?: () => void;
}

const TagList = ({
  index,
  tagField,
  onSelectionUpdate,
  hideHeader = false,
  setHidefields,
  navigationScreen = 'userAppliedTag',
  defaultSelectedTags = [],
  addNewTagToList,
  activeDocs,
  indexName,
  onMutationSuccess,
}: ITagList) => {
  const [selectedTagging] = useState<string>('tag');
  const [selectedTags, setSelectedTags] = useState<ITag[]>(defaultSelectedTags);
  const [open, setOpen] = useState(true);
  const [isError] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [selectedTagField, setSelectedTagField] = useState<
    IFieldModal | undefined
  >(tagField);
  const {updateTagName, isLoading} = useUpdateTagNames(true);
  const selectedTagsRef = useRef<ITag[]>([]);
  useEffect(() => {
    selectedTagsRef.current = selectedTags;
  }, [selectedTags]);
  const openCreateTagPanel = Boolean(anchorEl);
  const handleOpenPopover = (event: React.MouseEvent<HTMLElement>) => {
    if (navigationScreen === 'selectFieldAndApplyTag') {
      setHidefields?.(true);
    }
    setAnchorEl(event.currentTarget);
  };
  const {hasCreateOrEditTagFieldsPerm} = usePermissionsByPage(
    PAGE_NAME.ECA_DOC_CONTAINER
  );
  const handleClosePopover = (tag?: ITag) => {
    if (navigationScreen === 'selectFieldAndApplyTag') {
      setHidefields?.(false);
    }
    if (tag && navigationScreen === 'userAppliedTag') {
      const allTag = [...selectedTags, tag];
      updateTagName('tag', indexName ?? '', {
        allDocuments: false,
        documentIds: activeDocs ? [activeDocs] : [],
        searchId: null,
        tagFieldId: tagField?.fieldId,
        tagIds: allTag.map((tags) => tags.tagValueId),
      }, onMutationSuccess);
    }
    if (tag) {
      const setTag = (prev: ITag[]) => [tag, ...prev];
      addNewTagToList(tag);
      setSelectedTags(setTag);
      setSelectedTagField((prev) =>
        prev ? {...prev, tagValues: [...prev.tagValues, tag]} : undefined
      );
    }
    setAnchorEl(null);
  };

  const handleClick = () => {
    setOpen(!open);
  };

  const renderSubHeader = () => {
    return hasCreateOrEditTagFieldsPerm ? (
      <AurButton
        type="continue"
        size="Small"
        title={t(`ecaDocList.bulkTagging.createTag.createTag`)}
        label={t(`ecaDocList.bulkTagging.createTag.createTag`)}
        startIcon={<PlusIcon aria-hidden="true" />}
        classNames="small-border-radius-button"
        onClick={(e) => handleOpenPopover(e)}
      />
    ) : (
      <></>
    );
  };

  const nextDisplayOrder = useMemo(() => {
    return getNextDisplayOrder(tagField.tagValues);
  }, [tagField.tagValues]);

  const updateTagging = async (
    taggingType: string,
    requestBody: IUpdateTagNamesBody,
    onError: () => void,
    onSuccess?: () => void
  ) => {
    try {
      await updateTagName(
        taggingType,
        indexName ?? '',
        requestBody,
        onSuccess,
        onError
      );
    } catch (error) {
      console.warn(error);
      onError(); // Ensure `onError` is always called in case of failure
    }
  };

  const updateSelectedTags = useCallback(
    async (
      tags: ITag[],
      taggingType: string,
      targetItem: ITag | undefined,
      allSelect: string | undefined
    ) => {
      if (navigationScreen === 'userAppliedTag') {
        const preSelectedTagsRef = selectedTagsRef.current;

        let tagIds;
        if (allSelect && allSelect === 'check') {
          tagIds = tags.map((tag) => tag.tagValueId); // Selects all tagIds on master checkbox check operation
        } else if (allSelect && allSelect === 'unCheck') {
          tagIds = preSelectedTagsRef.map((tag) => tag.tagValueId); // Selects all tagIds on master checkbox uncheck operation
        } else {
          tagIds = targetItem
            ? [targetItem.tagValueId]
            : tags.map((tag) => tag.tagValueId); // Selects single tagId on indivisual check/uncheck operation
        }
        setSelectedTags(tags);
        const requestBody = {
          allDocuments: false,
          documentIds: activeDocs ? [activeDocs] : [],
          searchId: null,
          tagFieldId: tagField?.fieldId,
          tagIds: tagIds,
        };

        const onError = () => {
          setSelectedTags((prevTags) => {
            if (taggingType === 'tag') {
              return allSelect === 'check' ? [] : prevTags.slice(0, -1);
            }
            return allSelect === 'unCheck'
              ? preSelectedTagsRef
              : [...prevTags, ...(targetItem ? [targetItem] : [])];
          });
        };

        await updateTagging(
          taggingType,
          requestBody,
          onError,
          onMutationSuccess
        );
      } else {
        setSelectedTags(tags);
        onSelectionUpdate && onSelectionUpdate(tags);
      }
    },
    [
      activeDocs,
      indexName,
      navigationScreen,
      onMutationSuccess,
      onSelectionUpdate,
      selectedTagging,
      tagField?.fieldId,
      updateTagging,
    ]
  );

  return (
    <>
      {!openCreateTagPanel ? (
        <List component="nav" className="tag-list" key={`tag-field-${index}`} aria-label={tagField?.fieldLabel}>
          {!hideHeader && isLoading && (
            <Box className="blank-area-loader">
              <AurLoadingIndicator show={true} />
            </Box>
          )}
          {!hideHeader && (
            <Box
              className={`list-header-container ${!open && 'tag-field-hide'}`}
            >
              <Box className="header">
                <Typography
                  className={`typo-text subhead3_size list_field_label`}
                >
                  {tagField?.fieldLabel}
                </Typography>
                <AurButton
                  type="secondary"
                  size="Small"
                  title={open ? t('common.hide') : t('common.show')}
                  label={open ? t('common.hide') : t('common.show')}
                  onClick={handleClick}
                  classNames="small-border-radius-button"
                />
              </Box>
              {!open && (
                <Box>
                  <Typography className="typo-text body3_size">
                    {selectedTags.length}{' '}
                    {t('eca.documentPreviewer.taggingPanel.tagsSelected')}
                  </Typography>
                </Box>
              )}
            </Box>
          )}
          <Collapse in={open} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              <Box className="expand-tag-list">
                <AurTagSelectList
                  onSelectionChange={updateSelectedTags}
                  selectedTags={selectedTags}
                  renderSubHeader={() => renderSubHeader()}
                  items={tagField.tagValues}
                  selectAllLabel={t(
                    'eca.documentPreviewer.taggingPanel.selectAllWithCount',
                    {
                      selectedCount: selectedTags.length,
                    }
                  )}
                  tagAction={selectedTagging}
                  isError={isError}
                  height={360}
                  showSearchLabel={false}
                  searchPlaceHolder={t('common.search')}
                />
              </Box>
            </List>
          </Collapse>
        </List>
      ) : (
        selectedTagField && (
          <AddNewTag
            open={openCreateTagPanel}
            onClose={handleClosePopover}
            selectedTagField={selectedTagField}
            displayOrder={nextDisplayOrder}
          />
        )
      )}
    </>
  );
};

export default TagList;
